import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Typography, Tabs, Tab } from '@mui/material';
import styled from '@emotion/styled';
import BaseWidget from './BaseWidget';
import { useFetchQueue } from '../hooks/useFetchQueue';
import { requestGetTasksByUser } from '../redux/sagas/requests/task';
import { statusSeverity } from '../utils/statusColors';

const resolveTaskSeverity = (value) => {
    const deadline = statusSeverity('deadline', value);
    return deadline !== 'default' ? deadline : statusSeverity('priority', value);
};

const DataSet = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
}));

const DataSetItem = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    marginBottom: theme.spacing(1),
}));

const DatasetItemTitle = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.primary,
    textAlign: 'left',
    marginRight: theme.spacing(2),
}));

const DataSetItemValue = styled(Typography)(({ theme, statustype }) => ({
    fontWeight: theme.typography.fontWeightMedium,
    color: 
        statustype === 'error' ? theme.palette.error.main : 
        statustype === 'warning' ? theme.palette.warning.main : 
        statustype === 'primary' ? theme.palette.primary.main :
        theme.palette.success.main,
    textAlign: 'right',
}));

const EmptyState = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '150px',
    color: theme.palette.text.secondary,
}));

const CompactTabs = styled(Tabs)({
    minHeight: '36px',
});

const CompactTab = styled(Tab)(({ theme }) => ({
    minHeight: '36px',
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
}));

const TaskSummaryWidget = ({ widgetId, config = {}, onRemove }) => {
    const userId = useSelector(state => state.auth.userId);
    const [activeTab, setActiveTab] = useState(0);

    const { data, isLoading, error } = useFetchQueue(widgetId, {
        apiCall: () => requestGetTasksByUser(userId),
        cacheKey: `tasks-by-user-${userId}`,
        enabled: !!userId,
        transform: (response) => ({
            statusCount: response?.statusCount || {},
            priorityCount: response?.priorityCount || {},
        }),
    });

    const tasksStatus = data?.statusCount || {};
    const tasksPriority = data?.priorityCount || {};

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const hasData = Object.keys(tasksStatus).length > 0 || Object.keys(tasksPriority).length > 0;

    const tabActions = (
        <CompactTabs
            value={activeTab}
            onChange={handleTabChange}
        >
            <CompactTab label="Priority" />
            <CompactTab label="Status" />
        </CompactTabs>
    );

    return (
        <BaseWidget
            title="Task Summary"
            widgetId={widgetId}
            onRemove={onRemove}
            isLoading={isLoading}
            error={error}
            actions={tabActions}
        >
            {!hasData ? (
                <EmptyState>
                    <Typography variant="body2">
                        No tasks assigned
                    </Typography>
                </EmptyState>
            ) : (
                <>
                    {activeTab === 0 && (
                        <DataSet>
                            {Object.entries(tasksPriority).map(([priority, count], index) => (
                                <DataSetItem key={index}>
                                    <DatasetItemTitle variant="body1">{priority}</DatasetItemTitle>
                                    <DataSetItemValue variant="h6" statustype={resolveTaskSeverity(priority)}>
                                        {count}
                                    </DataSetItemValue>
                                </DataSetItem>
                            ))}
                        </DataSet>
                    )}
                    
                    {activeTab === 1 && (
                        <DataSet>
                            {Object.entries(tasksStatus).map(([status, count], index) => (
                                <DataSetItem key={index}>
                                    <DatasetItemTitle variant="body1">{status}</DatasetItemTitle>
                                    <DataSetItemValue variant="h6" statustype={resolveTaskSeverity(status)}>
                                        {count}
                                    </DataSetItemValue>
                                </DataSetItem>
                            ))}
                        </DataSet>
                    )}
                </>
            )}
        </BaseWidget>
    );
};

export default TaskSummaryWidget;
